<template>
	<div v-if="order">
		<landing-page-navbar></landing-page-navbar>

		<div class="booking-view">
			<div class="content-container">
				<div class="content h-auto">
					<!-- Loading state -->
					<div class="mt-5" v-if="loading && !showForm">
						<loading-state class=""></loading-state>
						<h4 class="mb-4 text-center">{{ $t('bp.ManageBooking.loading') }}</h4>
					</div>

					<template v-if="!loading">
						<!-- Order not found -->
						<div class="container" v-if="showForm || (!order && order.details)">
							<div class="card">
								<div class="card-body pb-0">
									<div class="alert alert-danger mb-0" v-if="error.messages.length > 0">
										<ul class="mb-0">
											<li v-for="(error, index) in error.messages" :key="index">
												{{ error }}
											</li>
										</ul>
									</div>
								</div>
								<div class="card-header">
									<h4>{{ $t('bp.ManageBooking.findBooking') }}</h4>
								</div>
								<div class="card-body">
									<div class="form-group">
										<label>{{ $t('bp.ManageBookingIndex.enterEmail') }}</label>
										<input type="email" class="form-control" v-model="form.email" placeholder="email@address.com" :class="{'is-invalid': error.email}" v-on:keyup.enter="findOrder()">
										<div class="invalid-feedback" v-if="error.email">{{ $t('bp.ManageBookingIndex.emailMissing') }}</div>
									</div>
									<div class="form-group">
										<label>
                                            {{ $t('bp.ManageBookingIndex.enterOrder') }} &nbsp;
											<span class="text-link text-sm cursor-pointer text-underline" v-b-popover.hover.top="$t('bp.ManageBookingIndex.whereToFindOrderAnswer')">{{ $t('bp.ManageBookingIndex.whereToFindOrder') }}</span>
										</label>
										<input type="text" class="form-control" inputmode="numeric" pattern="[0-9]*" v-model="form.orderRef" placeholder="e.g. 123456" :class="{'is-invalid': error.orderRef}" v-on:keyup.enter="findOrder()">
										<div class="invalid-feedback" v-if="error.orderRef">{{ $t('bp.ManageBookingIndex.orderMissing') }}</div>
									</div>
									<button class="btn continue-btn btn-block btn-lg" @click="findOrder()">{{ $t('bp.ManageBookingIndex.findOrder') }}</button>
								</div>
							</div>
						</div>

						<!-- Order details -->
						<div class="container" v-if="!showForm && (order && order.details)">

							<!-- Title -->
							<div class="title">
								<h1>{{ $t('bp.ManageBooking.manageMyBooking') }}</h1>
							</div>

							<!-- Covid banner -->
<!--							<div class="alert alert-warning">-->
<!--								<div class="alert-content">-->
<!--									<h5 class="mb-3">COVID-19 - Important changes to our service</h5>-->
<!--									<p class="mb-2">-->
<!--										We will call you on the day of your appointment to discuss your individual situation.-->
<!--									</p>-->
<!--									<p class="mb-2">-->
<!--										<strong>Please ensure you have your phone with you and you are contactable.</strong>-->
<!--									</p>-->
<!--									<p class="mb-0">-->
<!--										If we are unable to speak to you before your appointment, we may need to cancel your repair.-->
<!--									</p>-->
<!--								</div>-->
<!--							</div>-->

							<!-- Proof of purchase not supplied alert -->
							<div class="alert alert-info text-sm" v-if="isEditable && order.details.in_warranty">
								<i class="fa fa-info-circle"></i> <div class="alert-content">{{ $t('bp.ManageBooking.proofOfPurchaseMessage') }}</div>
							</div>

							<!-- "Order is not editable" alert -->
							<div class="alert alert-info" v-if="!isEditable">
								<i class="fa fa-info-circle"></i> <div class="alert-content">{{ $t('bp.ManageBooking.cannotChangeAppointment') }}</div>
							</div>

							<!-- Repair address -->
							<div class="card">
								<div class="card-header">
									<h4>{{ $t('bp.ManageBooking.repairAddress') }}</h4>
									<span class="cursor-pointer text-sm text-underline" v-if="isEditable && countryCodeSetting === 'GB'" v-b-modal.change-address-modal>{{ $t('bp.ManageBooking.changeAddress') }}</span>
								</div>
								<div class="card-body pb-2">
									<dl>
										<dt>{{ $t('bp.ManageBooking.repairLocation') }}</dt>
										<dd>{{ order.details.address_1 }}</dd>
										<dd v-if="order.details.address_2">{{ order.details.address_2 }}</dd>
										<dd>{{ order.details.town }}</dd>
										<dd class="text-uppercase">{{ order.details.postcode }}</dd>
									</dl>
								</div>
							</div>

							<!-- Booking overview -->
							<div class="card">
								<div class="card-header">
									<h4>{{ $t('bp.ManageBooking.bookingOverview') }}</h4>
									<span class="cursor-pointer text-sm text-underline" v-if="isEditable" v-b-modal.change-date-modal>{{ $t('bp.ManageBooking.changeDate') }}</span>
								</div>
								<div class="card-body pb-2">
                                    <dl>
                                        <dt>{{ $t('bp.ManageBooking.orderReference') }}</dt>
                                        <dd>{{ order.details.uuid }}</dd>
                                    </dl>
									<dl>
										<dt>{{ $t('bp.ManageBooking.repairDate') }}</dt>
										<dd>{{ repairDate }}</dd>
									</dl>
									<dl>
										<dt>{{ $t('bp.ManageBooking.phoneNumber') }}</dt>
										<dd>{{ order.details.phone_number }}</dd>
									</dl>
									<dl>
										<dt>{{ $t('bp.ManageBooking.email') }}</dt>
										<dd>{{ order.details.email }}</dd>
									</dl>
								</div>
							</div>

							<!-- Devices to be repaired -->
							<div class="card" v-if="orderDevices.length > 0">
								<div class="card-header">
									<h4>{{ $t('bp.ManageBooking.devicesToBeRepaired') }}</h4>
								</div>

								<loading-state v-if="loadingDevices"></loading-state>

								<div class="card-body pb-2" v-if="!loadingDevices">

									<!-- Device list -->
									<div class="basket-item" v-for="device in orderDevices" :key="device.id">
										<div class="media" v-if="device.model_version">
											<div class="media-left">
												<img :src="device.model_version.image.path" alt="Device Image" class="img-2x" />
											</div>
											<div class="media-body">
												<div class="align-items-center w-100">
													<h5>{{ device.model_version.title }} {{device.model_version.colour.title}}</h5>
												</div>
												<div>
													<p class="text-sm text-muted mt-1 mb-0" v-if="isValidDeviceForImei(device)">
														IMEI: {{ (device.imei) ? device.imei : 'Not Supplied' }}
													</p>
													<p class="text-sm text-muted mt-1 mb-0">
                                                        {{ $t('bp.ManageBooking.proofOfPurchase') }}:
														<span>{{ $t('bp.ManageBooking.popNotSupplied') }}</span>
														<span class="d-none">{{ $t('bp.ManageBooking.popSupplied') }}</span>
														<span class="d-none">&nbsp;<animated-loading size="xs" class="d-inline-block mx-1"></animated-loading> {{ $t('bp.ManageBooking.uploading') }}</span>
														<span class="d-none">&nbsp;<animated-error size="xs" class="d-inline-block mx-1"></animated-error> {{ $t('bp.ManageBooking.imageError') }}</span>
													</p>
												</div>
												<div class="price-table table">
													<div class="pricing-container">
														<div class="tr" v-for="repair in device.details" :key="repair.id">
															<div class="td">
																{{repair.problem.title}}
															</div>
														</div>
													</div>
												</div>
											</div>
											<div class="media-right text-right">
												<button class="btn btn-outline-dark btn-rounded btn-sm" v-if="isValidDeviceForImei(device) && isEditable && !device.imei" @click="showImeiModel(device)">{{ $t('bp.ManageBooking.addImei') }}</button>
												<br />
												<template v-if="order.details.in_warranty && !device.pop_supplied">
													<input type="file" name="proofOfPurchaseUpload" id="proofOfPurchaseUpload" class="file-input" accept="image/*,application/pdf" @change="uploadProofOfPurchase" :device-id="device.id">
													<label v-if="!uploadingPop" class="btn btn-outline-dark btn-rounded btn-sm mt-2" for="proofOfPurchaseUpload">{{ $t('bp.ManageBooking.addProofOfPurchase') }}</label>
													<label v-if="uploadingPop" class="btn btn-outline-dark btn-rounded btn-sm mt-2 disabled"><animated-loading class="mr-2" size="xs"></animated-loading>  {{ $t('bp.ManageBooking.uploading') }}</label>
												</template>
											</div>
										</div>
									</div>
									<div class="alert alert-info text-sm" v-if="carrierDetails.hasCallOutCharge">
										<div class="alert-content">
                                            {{ $t('bp.ManageBooking.supplyImeiWarningMessage') }}
										</div>
									</div>
								</div>
							</div>

							<!-- Cancel order -->
<!--							<div class="card" v-if="isEditable">-->
							<div class="card" v-if="countryCodeSetting === 'DE'">
								<div class="card-header">
									<h4>{{ $t('bp.ManageBooking.cancelAppointmentTitle') }}</h4>
								</div>
								<div class="card-body">
									<p>
                                        {{ $t('bp.ManageBooking.cancelAppointmentMessage') }}.
									</p>
									<button class="btn btn-outline-danger btn-rounded btn-sm btn-cancel-appointment" v-b-modal.cancel-booking-modal>{{ $t('bp.ManageBooking.cancelAppointmentButton') }}</button>
								</div>
							</div>

							<!-- Cancel booking modal -->
							<cancel-booking-modal :order="order" @order-cancelled="orderCancelled"></cancel-booking-modal>

							<!-- Change address modal -->
							<change-address-modal :order="order"></change-address-modal>

							<!-- Change date modal -->
							<change-date-modal :order="order" @dateChanged="loadOrderDetails()"></change-date-modal>

							<!-- IMEI input modal -->
							<imei-input-modal ref="imei-input-modal" :deviceId="imeiDeviceId" :order="order" @imei-added="loadOrderDetails"></imei-input-modal>

						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {mapActions, mapGetters} from "vuex"
import moment from 'moment'

export default {
	data() {
		return {
			cancelledOrder: null,
			error: {
				email: false,
				messages: [],
				orderRef: false,
			},
			form: {
				email: null,
				orderRef: null,
			},
			imeiDeviceId: null,
			loading: true,
			loadingDevices: true,
			showForm: false,
			uploadingPop: false,
		}
	},

	mounted() {
		this.loading = true;
		this.loadOrderDetails();
	},

	computed: {
		...mapGetters([
			"carrier",
			"carrierDetails",
			"order",
			"orderDevices",
			"currencySymbol",
      "countryCodeSetting"
		]),

		/** Check if we can edit the booking */
		isEditable() {
			let self = this;
			let bookingDate = new Date(this.order.booking.booking_date);
			let todaysDate = new Date();

			if (bookingDate <= todaysDate) {
				return false;
			}

			if (this.order.booking.booking_date === this.todayDate) {
				return false;
			}

			if (this.order.details.order_status !== "success") {
				self.cancelledOrder = true;
				return false;
			}

			if (this.cancelledOrder) {
				return false;
			}

			return true;
		},

		orderRef() {
			return this.$route.query.order_ref;
		},

		/** Format the repair date */
		repairDate() {
      let code = this.countryCodeSetting
      let date = moment(this.order.booking.booking_date);
      date.locale(code);
      return date.format('dddd Do MMMM')
		},

		/** Todays date */
		todaysDate() {
			let date = new Date();
			let month = date.getUTCMonth() + 1;
			let day = date.getDay();

			return date.getUTCFullYear() + "-" + month + "-" + day;
		},
	},

	methods: {
		...mapActions([
			"checkBookingIsComplete",
			"displayToast",
			"retrieveOrder",
			"retrieveOrderDevices",
			"uploadOrderDeviceProofOfPurchase"
		]),

		/** Search for an order **/
		findOrder() {
			this.error.email = false;
			this.error.orderRef = false;
			this.error.messages = [];

			if (!this.form.email) {
				this.error.email = true;
			}

			if (!this.form.orderRef) {
				this.error.orderRef = true;
			}

			if (this.form.email && this.form.orderRef) {
				this.loadOrderDetails();
			}
		},

		isValidDeviceForImei(device) {
			return device.device_type === "phone";
		},

		/** Loads the order and device details **/
		loadOrderDetails() {
			this.loading = true;
			this.showForm = false;
			if (
				(this.$route.query.order_ref && this.$route.query.email) ||
				(this.form.orderRef && this.form.email)
			) {
				let orderRef = (this.form.orderRef) ? this.form.orderRef : this.orderRef;
				let email = (this.form.email) ? this.form.email : this.$route.query.email;

				this.retrieveOrder({
					orderRef: orderRef,
					email: email
				}).then(() => {
					if (email !== this.order.details.email || orderRef != this.order.details.uuid) {
						this.loading = false;
						this.showForm = true;
                        if (this.countryCodeSetting === 'DE') {
                            this.error.messages.push("Es tut uns leid, wir konnten keine Buchung mit dieser E-Mail und Bestell-ID finden.");
                        } else {
                            this.error.messages.push("Sorry, we couldn't find a booking with that email and order ID.");
                        }
						return;
					}
					this.showForm = false;
					this.loadingDevices = true;
					this.loading = false;
					this.retrieveOrderDevices({
						orderRef: orderRef,
						email: email
					}).then(() => {
						this.loadingDevices = false;
					});
				}).catch(error => {
					this.loading = false;
					this.showForm = true;
					this.error.messages.push(error.response.data.messages[0]);
				});
			} else {
				if (Object.keys(this.$route.query).length > 0 && (!this.$route.query.order_ref || !this.$route.query.email)) {
                    if (this.countryCodeSetting === 'DE') {
                        this.error.messages.push("Entschuldige, wir konnten deine Buchung nicht finden. Gib deine Buchungsdetails in das jeweilige Feld ein.");
                    } else {
                        this.error.messages.push("Sorry, we couldn't find your booking. Enter your details below to try and find it.");
                    }
				}
				this.loading = false;
				this.showForm = true;
			}
		},

		/** Run when order is cancelled */
		orderCancelled() {
			this.loadOrderDetails();
			this.cancelledOrder = true;
		},

		/** Show the IMEI modal */
		showImeiModel(device) {
			this.imeiDeviceId = device.id;
			this.$bvModal.show("imei-input-modal");
		},

		/** Upload the proof of purchase for a device */
		uploadProofOfPurchase(e) {
			if (e.target.files.length > 1) {
				return;
			}

			let deviceId = e.currentTarget.getAttribute('device-id');

			let formData = new FormData();
			formData.append("file", e.target.files[0]);
			formData.append("type", "proof-of-purchase");

			this.uploadingPop = true;

			this.uploadOrderDeviceProofOfPurchase({
				deviceId: deviceId,
				formData: formData
			}).then(() => {
				this.uploadingPop = false;
				this.fetchDevices();
				this.displayToast({text: this.$t('bp.Toast.pop'), type: 'success', duration: 8000});
			}).catch(() => {
				this.uploadingPop = false;
				this.displayToast({text: this.$t('bp.Toast.popError'), type: 'error', duration: 8000});
			});

			document.getElementById("proofOfPurchaseUpload").value = "";
		},
	}
}
</script>